import {
  type DropdownOption,
  ChakraBox,
  ChakraFlex,
  FireflyButton,
  ModalV2,
  useToastMessage,
  ActionBar,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  useGetAppointmentTypesQuery,
  useLazyGetAccountsQuery,
} from '~/store/services/schedule';
import {
  useCancelAppointmentMutation,
  useLazyGetAppointmentDateTimeDetailsQuery,
  useSaveAppointmentMutation,
} from '~/store/services/appointment';
import { useGetPatientContactsQuery } from '~/store/services/patients';
import { useAppointmentDispatch } from '~/store/reducers/appointment';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import {
  ParticipantTypes,
  EDIT_ERRORS,
} from '~/lib/constants/schedule.constants';
import {
  MINIMUM_PROVIDER_ATTENDEES_WARNING,
  NO_PATIENT_OR_SUPPORT_ATTENDEE_WARNING,
  RecurringAppointmentUpdateOption,
} from '~/lib/constants/calendar.constants';
import { EDIT_APPOINTMENT_OPTION } from '~/lib/constants/appointment.constants';
import util, { getCategorizedPatientAndSupportAppointments } from '../util';
import { Recurrence } from '~/lib/util/calendar.util';
import { DateTime } from 'luxon';
import DiscardChangesModalV2 from '../../DiscardChangesModalV2';
import EditAppointmentSlotSelectionStep from './EditSlotSelectionStep';
import { ConflictErrors } from '../SchedulerV2';
import {
  formatAppointmentTimeRange,
  getCurrentAppointmentDetails,
  noEligibleHostErrorMessage,
} from '~/lib/util/schedule/schedule.util';
import ChangeHostConfirmationModal from '../Modals/ChangeHostConfirmationModal';
import {
  type ScheduledAppointments,
  EditSteps,
  getAppointmentData,
  getAttendingParticipants,
  getCaption,
  getCheckedParticipants,
  getEventTitle,
  getExternallyAddedProviders,
  isNewDateGreaterThanOldDate,
  setDefaultHost,
} from './util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { capitalizeFirstLetter } from '~/lib/common';
import {
  EditAppointmentEvents,
  EditAppointmentSource,
} from '~/analytics/patientEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import EditRecurrenceStep from './EditRecurrenceStep';
import EditAppointmentSummary from './EditAppointmentSummary';
import { AppointmentAttendeesAllowed } from '~/lib/constants/scheduling.constants';

const TOP_LEVEL_ID = 'edit-appointment';

interface EditAppointmentModalProps {
  patientCareTeams: CareTeamMember[];
  editAppointment: ScheduleItemBase;
  onClose: (appointmentStartDateTime?: string) => void;
}

export type RenderCurrentAppointmentDateProps = {
  appointmentTime: string;
  currentAppointmentDetails: string;
};

export const renderCurrentAppointmentDate = ({
  appointmentTime,
  currentAppointmentDetails,
}: RenderCurrentAppointmentDateProps) => {
  return (
    <ChakraFlex
      alignItems="center"
      backgroundColor="neutral.background.secondary"
      borderRadius="12px"
      gap="8px"
      id={`${TOP_LEVEL_ID}__current-appointment-bar`}
      padding="8px 12px"
    >
      <Icon color="black" name={FAIconName.calendarRegular} size="sm" />
      <ChakraFlex alignItems="center" gap="8px">
        <Text variant="captionMedium">Current appointment:</Text>
        <ChakraFlex gap="8px">
          <Text variant="caption">{currentAppointmentDetails}</Text>
          <Text color="neutral.secondary" variant="caption">
            {appointmentTime}
          </Text>
        </ChakraFlex>
      </ChakraFlex>
    </ChakraFlex>
  );
};

const EditAppointmentModal = ({
  patientCareTeams,
  editAppointment,
  onClose,
}: EditAppointmentModalProps) => {
  const { trackEvent } = useAnalytics();
  const { createToast } = useToastMessage();

  const now = DateTime.now();

  const { attendees, title, duration, patient } = editAppointment;
  const { permissions, currentUser, calendarAccount } = useGlobalContext();
  const host = attendees.find((a) => a.isHost);
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const { providerTypeName: myProviderTypeName } = currentUser;
  const { profileConnected: isMyProfileConnected } = calendarAccount ?? {};

  const [
    fetchAppointmentDateTimeDetails,
    { data: patientAppointmentsDateTimeData },
  ] = useLazyGetAppointmentDateTimeDetailsQuery();

  const { data: supports } = useGetPatientContactsQuery(patient.userId, {
    skip: !patient.userId,
  });

  const { data: appointmentTypes } = useGetAppointmentTypesQuery();
  const [
    getCareTeamMemberCalendarAccounts,
    { data: careTeamMemberCalendarAccounts },
  ] = useLazyGetAccountsQuery();

  const [shouldEditAllAppointments, setShouldEditAllAppointments] =
    useState<RecurringAppointmentUpdateOption>(
      RecurringAppointmentUpdateOption.ONLY,
    );
  const [step, setStep] = useState<EditSteps>(
    editAppointment.isRecurring
      ? EditSteps.RECURRENCE_SELECT
      : EditSteps.SLOT_SELECT,
  );

  const [newAppointmentType, setNewAppointmentType] =
    useState<AppointmentType>(null);

  const [hostAttendee, setHostAttendee] = useState<Participant>(null);
  const [patientAttendee, setPatientAttendee] = useState<Participant>(null);
  const [supportAttendees, setSupportAttendees] = useState<Participant[]>([]);
  const [providerAttendees, setProviderAttendees] = useState<Participant[]>([]);
  const [externalAttendees, setExternalAttendees] = useState<Participant[]>([]);
  const [currentAppointmentTime, setCurrentAppointmentTime] = useState<string>(
    formatAppointmentTimeRange(
      editAppointment.startDateTime,
      editAppointment.endDateTime,
    ),
  );

  const [currentAppointmentDetails, setCurrentAppointmentDetails] =
    useState<string>(
      getCurrentAppointmentDetails(
        editAppointment.startDateTime,
        editAppointment.frequency,
        editAppointment.recurringUntilDateTime,
      ),
    );

  const [isEditEventWarningModalOpen, setIsEditEventWarningModalOpen] =
    useState<boolean>(false);
  const [appointmentStartDateTime, setAppointmentStartDateTime] =
    useState<string>(null);
  const [ignoreAvailabilitySettings, setIgnoreAvailabilitySettings] =
    useState<boolean>(false);
  const [isFailedToEdit, setIsFailedToEdit] = useState<boolean>(false);
  const [isFetchingConflicts, setIsFetchingConflicts] = useState(false);

  const [updatedFrequency, setUpdatedFrequency] = useState<Recurrence>(null);

  const handleWarningModalClose = () => {
    setIsEditEventWarningModalOpen(false);
  };

  const [availabilityQuery, setAvailabilityQuery] =
    useState<CalendarResourceConfigAvailabilityQuery>(null);

  const [elementToken, setElementToken] = useState<string>(null);

  const initialFrequency = (capitalizeFirstLetter(editAppointment.frequency) ??
    Recurrence.DOES_NOT_REPEAT) as Recurrence;

  const currentAppointmentType = appointmentTypes?.find(
    (eachAppointment) =>
      eachAppointment?.appointmentType === title &&
      eachAppointment?.durationInMinutes === duration,
  );

  const selectedAppointmentType = newAppointmentType ?? currentAppointmentType;

  const scheduledAppointments: ScheduledAppointments = useMemo(() => {
    return getCategorizedPatientAndSupportAppointments(
      patientAppointmentsDateTimeData,
    );
  }, [patientAppointmentsDateTimeData]);

  const createProviderAttendee = useCallback(
    (
      provider: CareTeamMember,
      calendarInfo: CalendarAccount,
      currentProvider: ScheduleUserBase,
    ) => ({
      id: provider.providerExternalId,
      isChecked: currentProvider && !currentProvider.isHost,
      isEligibleToHost: selectedAppointmentType.providerTypeNames.includes(
        provider.providerTypeName,
      ),
      primaryText: getPreferredFullName(
        undefined,
        provider.firstName,
        provider.lastName,
      ),
      secondaryText: getCaption({
        timezoneDetails: provider.timeZoneDetails,
        type: provider.providerTypeName,
      }),
      timezone: provider.timezone,
      ...(calendarInfo && {
        calendarId: calendarInfo.calendarId,
        sub: calendarInfo.sub,
      }),
      type: ParticipantTypes.PROVIDER,
    }),
    [newAppointmentType, selectedAppointmentType],
  );

  const createSupportAttendee = useCallback(
    (support: PatientContact) => ({
      id: support.supportExternalId,
      isChecked: attendees.some(
        (attendee) => attendee.externalId === support.supportExternalId,
      ),
      primaryText: getPreferredFullName(
        support.chosenName,
        support.firstName,
        support.lastName,
        true,
      ),
      secondaryText: getCaption({
        timezoneDetails: { label: support.timezone },
        type: support.relationToPatient,
      }),
      type: ParticipantTypes.SUPPORT,
    }),
    [attendees],
  );

  const createPatientAttendee = useCallback(
    () => ({
      id: patient.userId,
      isChecked: attendees.some(
        (attendee) => attendee?.type?.toLowerCase() === 'patient',
      ),
      primaryText: getPreferredFullName(
        patient.chosenName,
        patient.firstName,
        patient.lastName,
        true,
      ),
      secondaryText: getCaption({
        timezoneDetails: patient.timezoneDetails,
        type: 'Patient',
      }),
      timezone: patient.timezone,
      type: ParticipantTypes.PATIENT,
    }),
    [
      attendees,
      patient.userId,
      patient.name,
      patient?.timezoneDetails?.code,
      patient.timezone,
    ],
  );

  useEffect(() => {
    if (!patientCareTeams) return;

    const externallyAddedProviders = getExternallyAddedProviders({
      attendees,
      careTeamResponse: patientCareTeams,
    }).map((attendee) => attendee.externalId);

    const providerExternalIds = patientCareTeams.map(
      (member) => member.providerExternalId,
    );

    getCareTeamMemberCalendarAccounts({
      providerExternalIds: [
        ...providerExternalIds,
        ...externallyAddedProviders,
      ],
    });
  }, [patientCareTeams, getCareTeamMemberCalendarAccounts]);

  const setParticipants = (clearAppointment?: boolean) => {
    if (!patientCareTeams || !selectedAppointmentType) return;
    if (clearAppointment) setNewAppointmentType(null);
    if (host) {
      const hostAttendeeData = setDefaultHost({
        attendees,
        careTeamMemberCalendarAccounts,
        careTeamProviders: patientCareTeams,
        selectedAppointmentType,
      });
      setHostAttendee(hostAttendeeData);

      if (hostAttendeeData) {
        const providersWithCalendarInfo = patientCareTeams
          .filter(
            (provider) => provider.providerExternalId !== hostAttendeeData.id,
          )
          .map((provider) => {
            const calendarInfo = careTeamMemberCalendarAccounts?.find(
              (account) => account.externalId === provider.providerExternalId,
            );
            const currentProvider = attendees.find(
              (attendee) => attendee.externalId === provider.providerExternalId,
            );
            return createProviderAttendee(
              provider,
              calendarInfo,
              currentProvider,
            );
          })
          .filter(Boolean);

        const externallyAddedProviders = getExternallyAddedProviders({
          attendees,
          careTeamResponse: patientCareTeams,
        })
          .filter((attendee) => attendee.externalId !== hostAttendeeData.id)
          .map((attendee) => {
            return {
              calendarId: attendee.calendarId,
              id: attendee.externalId,
              isChecked: true,
              isEligibleToHost:
                selectedAppointmentType.providerTypeNames.includes(
                  attendee.providerType,
                ),
              primaryText: attendee.name,
              secondaryText: getCaption({
                timezoneDetails: attendee.timezoneDetails,
                type: attendee.type,
              }),
              sub: attendee.sub,
              timezone: attendee.timezoneDetails?.label,
              type: ParticipantTypes.PROVIDER,
            };
          });

        setProviderAttendees([
          ...providersWithCalendarInfo,
          ...externallyAddedProviders,
        ]);
      }
    }

    if (
      selectedAppointmentType.attendeesAllowed ===
      AppointmentAttendeesAllowed.ALL
    ) {
      // Set support attendees
      if (supports) {
        const finalSupports = supports
          .filter((support) => support.isSupport)
          .map(createSupportAttendee);
        setSupportAttendees(finalSupports);
      }

      // Set patient attendee
      setPatientAttendee(createPatientAttendee());
    } else {
      setSupportAttendees(null);
      setPatientAttendee(null);
    }
  };

  useEffect(() => {
    if (!patientCareTeams || !careTeamMemberCalendarAccounts) return;

    setProviderAttendees([]);
    setExternalAttendees([]);

    setParticipants();
  }, [
    careTeamMemberCalendarAccounts,
    createProviderAttendee,
    createSupportAttendee,
    createPatientAttendee,
    attendees,
    supports,
    selectedAppointmentType,
  ]);

  const eventTitle = getEventTitle({
    editAppointment,
    newAppointmentType,
    selectedAppointmentType,
  });
  const isLoading = false;

  const [
    saveAppointment,
    { isLoading: isSaveAppointmentLoading, reset: resetSaveAppointmentError },
  ] = useSaveAppointmentMutation();

  const { setAppointmentCurrentDate: setCurrentDate } =
    useAppointmentDispatch();

  const [deleteAppointment, { isLoading: isCancelAppointmentLoading }] =
    useCancelAppointmentMutation();

  const [
    isChangeHostConfirmationModalOpen,
    setIsChangeHostConfirmationModalOpen,
  ] = useState(false);
  const [newHostId, setNewHostId] = useState<string>(null);

  useEffect(() => {
    if (shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL) {
      setExternalAttendees([]);
      setParticipants(true);
    }
    setAppointmentStartDateTime(null);
    setIgnoreAvailabilitySettings(false);
  }, [shouldEditAllAppointments]);

  useEffect(() => {
    if (
      !editAppointment.patient.userId ||
      (!isSchedulingAdmin && !(isMyProfileConnected && myProviderTypeName)) ||
      hostAttendee == null
    )
      return;
    const props = {
      endDateISO: now.plus({ months: 6 }).toISO(),
      patientId: editAppointment.patient.userId,
      startDateISO: now.minus({ hours: 2 }).toISO(),
    };

    if (patientAppointmentsDateTimeData) return;

    fetchAppointmentDateTimeDetails(props);
  }, [hostAttendee]);

  const sortedAppointmentTypes = useMemo<AppointmentType[]>(() => {
    if (!appointmentTypes) {
      return [];
    }

    return appointmentTypes
      .filter(
        (eachType) =>
          eachType?.appointmentGroup ===
          selectedAppointmentType?.appointmentGroup,
      )
      .sort((a, b) => a?.appointmentType.localeCompare(b?.appointmentType));
  }, [appointmentTypes, selectedAppointmentType?.appointmentGroup]);

  const appointmentTypeOptions = useMemo(() => {
    return sortedAppointmentTypes.map((type) => ({
      label: `${type?.appointmentType}, ${type?.durationInMinutes} minutes`,
      value: type?.appointmentTypeExtId,
    }));
  }, [sortedAppointmentTypes]);

  const handleToast = () => {
    createToast({
      icon: FAIconName.checkRegular,
      id: `${TOP_LEVEL_ID}__success`,
      text: 'Appointment edited',
      variant: 'success',
    });
  };

  const getRecurrenceValue = (): DropdownOption => {
    const frequency = updatedFrequency ?? initialFrequency;
    if (
      initialFrequency !== Recurrence.DOES_NOT_REPEAT &&
      shouldEditAllAppointments === RecurringAppointmentUpdateOption.ONLY
    ) {
      return {
        label: Recurrence.DOES_NOT_REPEAT,
        value: Recurrence.DOES_NOT_REPEAT,
      };
    }
    return {
      label: frequency === Recurrence.MONTHLY ? 'Every 4 weeks' : frequency,
      value: frequency,
    };
  };

  const getRecurrenceFrequency = (): Recurrence => {
    if (
      initialFrequency !== Recurrence.DOES_NOT_REPEAT &&
      shouldEditAllAppointments === RecurringAppointmentUpdateOption.ONLY
    )
      return Recurrence.DOES_NOT_REPEAT;
    return updatedFrequency ?? initialFrequency;
  };

  const editAppointmentEventTrackProps = {
    appointmentId: editAppointment.externalId,
    isEditingSeries:
      shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL,
    isRecurringEvent: editAppointment.isRecurring,
  };

  const handleSave = (): void => {
    saveAppointment(
      getAppointmentData({
        appointmentStartDateTime,
        appointmentType: selectedAppointmentType,
        attendees: {
          externalAttendees,
          hostAttendee,
          patientAttendee,
          providerAttendees,
          supportAttendees,
        },
        editAppointment,
        ignoreAvailabilitySettings,
        patient,
        recurrenceFrequency: getRecurrenceFrequency(),
      }),
    )
      .then(({ error }) => {
        if (error) {
          setIsFailedToEdit(true);
          return;
        }

        trackEvent(EditAppointmentEvents.editSaveChangesCTAClicked, {
          ...editAppointmentEventTrackProps,
          appointmentTitle: editAppointment.title,
          changeHost: !editAppointment.hosts.some(
            (host) => host?.externalId === hostAttendee?.id,
          ),
          ignoreAvailability: ignoreAvailabilitySettings,
          recurrence: getRecurrenceFrequency(),
          source: EditAppointmentSource.editAppointmentReviewDetailsScreen,
        });

        deleteAppointment({
          appointmentExternalId: editAppointment.externalId,
          cancellationReason: 'PROVIDER_EDIT',
          cancelType: shouldEditAllAppointments,
        }).then(() => {
          setCurrentDate(appointmentStartDateTime);
          handleToast();
          onClose(appointmentStartDateTime);
        });
      })
      .catch(() => setIsFailedToEdit(true));
  };

  const renderBackButton = (onClick: () => void) =>
    (editAppointment.isRecurring || step === EditSteps.APPOINTMENT_SUMMARY) && (
      <FireflyButton
        id={`${TOP_LEVEL_ID}__back-button`}
        leftIcon={FAIconName.chevronLeftRegular}
        onClick={onClick}
        variant="secondary"
      >
        Back
      </FireflyButton>
    );

  const renderPrimaryButton = ({
    text,
    onClick,
    icon,
    disabled,
    leftIcon = false,
    isLoading = false,
  }: {
    text: string;
    onClick: () => void;
    icon: FAIconName;
    disabled: boolean;
    leftIcon?: boolean;
    isLoading?: boolean;
  }) => (
    <ChakraBox width="288px">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__${text}-button`}
        isDisabled={disabled}
        isFullWidth
        isLoading={isLoading}
        onClick={onClick}
        {...(leftIcon ? { leftIcon: icon } : { rightIcon: icon })}
      >
        {text}
      </FireflyButton>
    </ChakraBox>
  );

  const goToStep = (targetStep: EditSteps) => {
    setStep(targetStep);
  };

  const handleRecurrenceStepPrimaryClick = () => {
    trackEvent(EditAppointmentEvents.editAppointmentCTAClicked, {
      ...editAppointmentEventTrackProps,
      source: EditAppointmentSource.editRecurrenceSelectionScreen,
    });
    goToStep(EditSteps.SLOT_SELECT);
  };

  const handleAvailabilitySelectionContinue = () => {
    trackEvent(EditAppointmentEvents.editContinueCTAClicked, {
      ...editAppointmentEventTrackProps,
      source: EditAppointmentSource.editAvailabilitySelectionScreen,
    });
    setStep(EditSteps.APPOINTMENT_SUMMARY);
  };

  const isSelectedDateGreaterThanAppointmentEndDate =
    isNewDateGreaterThanOldDate(
      appointmentStartDateTime,
      editAppointment.recurringUntilDateTime,
    );

  const shouldAllProvidersAvailable = useMemo(() => {
    return (
      selectedAppointmentType?.attendeesAllowed ===
      AppointmentAttendeesAllowed.PROVIDER_ONLY
    );
  }, [selectedAppointmentType]);

  const getErrorMessages = useMemo(() => {
    const errors = [];

    const hasProvidersOrSupport =
      providerAttendees.length > 0 || supportAttendees?.length > 0;
    if (!hasProvidersOrSupport) return [];

    if (!hostAttendee) {
      return [
        {
          conflit: noEligibleHostErrorMessage(
            util.joinStringsWithOr(selectedAppointmentType?.providerTypeNames),
          ),
          id: ConflictErrors.NO_ELIGIBLE_HOST,
        },
      ];
    }

    const patientChecked = patientAttendee?.isChecked;
    const anySupportChecked =
      getCheckedParticipants(supportAttendees).length > 0;

    if (shouldAllProvidersAvailable) {
      const selectedProviderIds = new Set<string>();

      if (hostAttendee.id) {
        selectedProviderIds.add(hostAttendee.id);
      }

      [...providerAttendees, ...externalAttendees]
        .filter((provider) => provider.isChecked)
        .forEach((provider) => selectedProviderIds.add(provider.id));

      if (selectedProviderIds.size < 2) {
        errors.push({
          conflit: MINIMUM_PROVIDER_ATTENDEES_WARNING,
          id: ConflictErrors.MISSING_ATTENDEES,
        });
      }
    } else if (!patientChecked && !anySupportChecked) {
      errors.push({
        conflit: NO_PATIENT_OR_SUPPORT_ATTENDEE_WARNING,
        id: ConflictErrors.MISSING_ATTENDEES,
      });
    }
    if (!hasProvidersOrSupport) return [];
    return errors;
  }, [
    hostAttendee,
    patientAttendee,
    supportAttendees,
    providerAttendees,
    externalAttendees,
    selectedAppointmentType,
    shouldAllProvidersAvailable,
  ]);

  const renderSlotSelectionBackButton = () => {
    return getErrorMessages.length > 0 && host ? (
      <ChakraFlex direction="column" gap="10px" maxWidth="612px">
        {getErrorMessages.map((eachError) => {
          return (
            <ActionBar
              barIcon={FAIconName.exclamationTriangleRegular}
              id={eachError.id}
              key={eachError.id}
              size="small"
              text={eachError.conflit}
              variant="warning"
            />
          );
        })}
      </ChakraFlex>
    ) : (
      renderBackButton(() => {
        trackEvent(EditAppointmentEvents.editBackCTAClicked, {
          ...editAppointmentEventTrackProps,
          source: EditAppointmentSource.editAvailabilitySelectionScreen,
        });
        goToStep(EditSteps.RECURRENCE_SELECT);
      })
    );
  };

  const handleAppointmentSummaryBackClick = () => {
    trackEvent(EditAppointmentEvents.editBackCTAClicked, {
      ...editAppointmentEventTrackProps,
      source: EditAppointmentSource.editAppointmentReviewDetailsScreen,
    });
    goToStep(EditSteps.SLOT_SELECT);
    resetSaveAppointmentError();
    setIsFailedToEdit(false);
  };

  const renderStepFooter = () => {
    const isDisabled =
      isLoading ||
      !appointmentStartDateTime ||
      getErrorMessages.length > 0 ||
      isSelectedDateGreaterThanAppointmentEndDate;

    switch (step) {
      case EditSteps.RECURRENCE_SELECT:
        return (
          <ChakraFlex height="40px" width="288px">
            {renderPrimaryButton({
              disabled: false,
              icon: FAIconName.angleRight,
              onClick: handleRecurrenceStepPrimaryClick,
              text: 'Edit appointment',
            })}
          </ChakraFlex>
        );
      case EditSteps.SLOT_SELECT:
        return (
          <ChakraFlex
            alignItems="center"
            height="40px"
            justifyContent="space-between"
            marginBottom="13px"
            width="100%"
          >
            <>
              {isSelectedDateGreaterThanAppointmentEndDate ? (
                <ChakraBox marginRight="24px">
                  <ActionBar
                    barIcon={FAIconName.exclamationTriangleRegular}
                    id={`${TOP_LEVEL_ID}__timeslot-selection-error`}
                    size="small"
                    text={EDIT_ERRORS.newDateGreaterThanOldEndDate}
                    variant="warning"
                  />
                </ChakraBox>
              ) : (
                <ChakraFlex gap="18px">
                  <ChakraBox>{renderSlotSelectionBackButton()}</ChakraBox>
                  {getErrorMessages.length === 0 &&
                    renderCurrentAppointmentDate({
                      appointmentTime: currentAppointmentTime,
                      currentAppointmentDetails,
                    })}
                </ChakraFlex>
              )}
              {renderPrimaryButton({
                disabled: isDisabled,
                icon: FAIconName.arrowRightRegular,
                onClick: handleAvailabilitySelectionContinue,
                text: 'Continue',
              })}
            </>
          </ChakraFlex>
        );
      case EditSteps.APPOINTMENT_SUMMARY:
        return (
          <ChakraFlex justifyContent="space-between" margin="6px" width="100%">
            {renderBackButton(handleAppointmentSummaryBackClick)}
            {renderPrimaryButton({
              text: 'Save changes',
              onClick: handleSave,
              icon: FAIconName.checkRegular,
              disabled: isFetchingConflicts,
              leftIcon: true,
              isLoading: isSaveAppointmentLoading || isCancelAppointmentLoading,
            })}
          </ChakraFlex>
        );
      default:
        return null;
    }
  };

  const renderFooter = () => (
    <ChakraFlex
      alignItems="center"
      justifyContent="space-between"
      marginTop="24px"
      width="100%"
    >
      {renderStepFooter()}
    </ChakraFlex>
  );
  const handleAppointmentTypeChange = (appointmentId: string) => {
    setIgnoreAvailabilitySettings(false);
    const newAppointmentTypeSelected = appointmentTypes.find(
      (type) => type.appointmentTypeExtId === appointmentId,
    );
    setAppointmentStartDateTime(null);
    setNewAppointmentType(newAppointmentTypeSelected);
  };

  const commonProps = useMemo(
    () => ({
      appointmentStartDateTime,
      editAppointment: {
        ...editAppointment,
        frequency: initialFrequency,
      },
      eventTitle,
      hostAttendee,
      id: TOP_LEVEL_ID,
      recurrenceOption: getRecurrenceValue(),
      selectedAppointmentType,
      shouldEditAllAppointments,
    }),
    [
      appointmentStartDateTime,
      eventTitle,
      hostAttendee,
      newAppointmentType,
      selectedAppointmentType,
      shouldEditAllAppointments,
      editAppointment,
      updatedFrequency,
    ],
  );

  const renderEditModalContent = () => {
    switch (step) {
      case EditSteps.RECURRENCE_SELECT:
        return (
          <EditRecurrenceStep
            onOptionChange={(e) => setShouldEditAllAppointments(e)}
            {...commonProps}
          />
        );
      case EditSteps.APPOINTMENT_SUMMARY:
        return (
          <EditAppointmentSummary
            attendingParticipants={getAttendingParticipants({
              externalAttendees,
              hostAttendee,
              patientAttendee,
              providerAttendees,
              supportAttendees,
            })}
            availabilityQuery={availabilityQuery}
            elementToken={elementToken}
            isFetchingConflicts={setIsFetchingConflicts}
            scheduledAppointments={scheduledAppointments}
            shouldShowEditErrorMessage={isFailedToEdit}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  const handleDiscardChangesConfirm = () => {
    let source;
    if (step === EditSteps.RECURRENCE_SELECT) {
      source = EditAppointmentSource.editRecurrenceSelectionScreen;
    } else if (step === EditSteps.SLOT_SELECT) {
      source = EditAppointmentSource.editAvailabilitySelectionScreen;
    } else {
      source = EditAppointmentSource.editAppointmentReviewDetailsScreen;
    }
    trackEvent(EditAppointmentEvents.editAppointmentDiscardChangesCTAClicked, {
      ...editAppointmentEventTrackProps,
      source,
    });
    onClose();
  };

  const handleChangeHostConfirmationModalClose = () => {
    setIsChangeHostConfirmationModalOpen(false);
    setNewHostId(null);
  };

  const handleChangeHostConfirmation = () => {
    const oldHost = { ...hostAttendee, type: ParticipantTypes.PROVIDER };
    const newHost = [...providerAttendees, ...externalAttendees].find(
      (each) => each.id === newHostId,
    );
    setHostAttendee({
      ...newHost,
      isChecked: true,
      type: ParticipantTypes.HOST,
    });

    const updateList = (list: Participant[], item: Participant) =>
      list
        .filter((participant) => participant.id !== newHostId)
        .concat({
          ...item,
          isChecked: false,
          isEligibleToHost: true,
        });
    if (externalAttendees.some((a) => a.id === newHostId)) {
      setExternalAttendees((prev) => updateList(prev, oldHost));
    } else {
      setProviderAttendees((prev) => updateList(prev, oldHost));
    }
    handleChangeHostConfirmationModalClose();
  };

  const handleParticipantSelect = (
    participantId: string,
    userType: ParticipantTypes,
  ) => {
    setAppointmentStartDateTime(null);
    const toggleParticipant = (prev: Participant[]) =>
      prev.map((participant) =>
        participant.id === participantId
          ? { ...participant, isChecked: !participant.isChecked }
          : participant,
      );
    switch (userType) {
      case ParticipantTypes.PROVIDER:
        setProviderAttendees(toggleParticipant);
        setExternalAttendees(toggleParticipant);
        break;
      case ParticipantTypes.SUPPORT:
        setSupportAttendees(toggleParticipant);
        break;
      case ParticipantTypes.PATIENT:
        setPatientAttendee((prev) => ({ ...prev, isChecked: !prev.isChecked }));
        break;
      case ParticipantTypes.HOST: {
        setNewHostId(participantId);
        setIsChangeHostConfirmationModalOpen(true);
        break;
      }
    }
  };

  const handleFrequencyChange = (frequency: DropdownOption) => {
    setUpdatedFrequency(frequency.value as Recurrence);
  };

  const onTimeZoneChange = (tzid?: string) => {
    setCurrentAppointmentTime(
      formatAppointmentTimeRange(
        editAppointment.startDateTime,
        editAppointment.endDateTime,
        tzid,
      ),
    );

    setCurrentAppointmentDetails(
      getCurrentAppointmentDetails(
        editAppointment.startDateTime,
        editAppointment.frequency,
        editAppointment.recurringUntilDateTime,
        tzid,
      ),
    );
  };

  const handleProviderAdding = (providers: Participant[]) => {
    setExternalAttendees(providers);
    setAppointmentStartDateTime(null);
  };

  return (
    <ModalV2
      footerContent={renderFooter()}
      id={TOP_LEVEL_ID}
      isOpen
      onClose={() => {
        setIsEditEventWarningModalOpen(true);
      }}
      size={step == EditSteps.RECURRENCE_SELECT ? 'sm' : '5xl'}
      title={EDIT_APPOINTMENT_OPTION}
    >
      {renderEditModalContent()}
      <ChakraBox display={step == EditSteps.SLOT_SELECT ? 'block' : 'none'}>
        <EditAppointmentSlotSelectionStep
          appointmentTypeOptions={appointmentTypeOptions}
          attendees={{
            externalAttendees,
            hostAttendee,
            patientAttendee,
            providerAttendees,
            supportAttendees,
          }}
          handleAppointmentTypeChange={handleAppointmentTypeChange}
          handleParticipantSelect={handleParticipantSelect}
          ignoreAvailabilitySettings={ignoreAvailabilitySettings}
          onAppointmentSlotSelect={setAppointmentStartDateTime}
          onFrequencyChange={handleFrequencyChange}
          onProviderAdded={handleProviderAdding}
          onTimeZoneChange={onTimeZoneChange}
          scheduledAppointments={scheduledAppointments}
          setAvailabilityQuery={setAvailabilityQuery}
          setElementToken={setElementToken}
          setHostAttendee={setHostAttendee}
          setIgnoreAvailabilitySettings={setIgnoreAvailabilitySettings}
          shouldAllProvidersAvailable={shouldAllProvidersAvailable}
          step={step}
          {...commonProps}
        />
      </ChakraBox>
      <DiscardChangesModalV2
        id={`${TOP_LEVEL_ID}__close-warning-modal`}
        isOpen={isEditEventWarningModalOpen}
        onClose={handleWarningModalClose}
        onConfirm={handleDiscardChangesConfirm}
      />
      <ChangeHostConfirmationModal
        isOpen={isChangeHostConfirmationModalOpen}
        onClose={handleChangeHostConfirmationModalClose}
        onConfirm={handleChangeHostConfirmation}
      />
    </ModalV2>
  );
};
export default EditAppointmentModal;
